/**
 * Tela de Resultado — mockup 15 (decisão UX 2026-05-19).
 *
 * Decisões:
 *   - Sem breakdown 3 escopos GHG (substituído por animação de faixa colorida)
 *   - Sem lista de categorias humanizadas
 *   - Contador anima 0→total em 2s ease-out + barra preenche simultaneamente
 *   - Coleta de email/consent é INLINE aqui (Step Submitter foi descontinuado)
 *   - `prefers-reduced-motion` honor: mostra valor final imediato + barra cheia
 *   - `aria-live="polite"` anuncia "Sua emissão é X tCO₂e, faixa Y"
 *   - 2 ações: "Compensar com este projeto" (se purchase) + "Receber por email"
 *     (sempre, inline form)
 */
import { useEffect, useRef, useState } from 'react';
import type { ComputeResponse, InstanceOfferingDetail, SubmitterInput } from '../api/types';
import { t } from '../App';
import { formatBRL, formatTCO2e } from '../lib/format';

interface Props {
  result: ComputeResponse;
  canPurchase: boolean;
  onCompensate: (offering: InstanceOfferingDetail) => void;
  onLeadOptIn?: (submitter: SubmitterInput) => Promise<void> | void;
  leadCaptured?: boolean;
  onRestart: () => void;
  i18n?: Record<string, string>;
  /**
   * Quando 'organizacao' o lead já foi capturado no Step 4 (B2B), então
   * o opt-in inline não aparece e a mensagem pós-captured fica em tom
   * comercial em vez de "relatório por e-mail" (mockup 20 vs 15).
   */
  audience?: 'participante' | 'organizacao';
}

// Escala progressiva de impacto (mockup 15 — "azul→verde→amarelo→laranja→vermelho").
// Track tem o gradiente inteiro fixo; a fill cresce até a posição da band, revelando
// parcialmente o gradiente. Posição (0-1) representa a fronteira superior da band.
const BAND_SCALE: Record<string, { position: number; chipBg: string; chipFg: string }> = {
  muito_baixa: { position: 0.25, chipBg: 'bg-[#4A8C5F]/15', chipFg: 'text-[#3F6A47]' },
  baixa:       { position: 0.50, chipBg: 'bg-[#B5A642]/20', chipFg: 'text-[#7E7427]' },
  media:       { position: 0.75, chipBg: 'bg-cobre/20',     chipFg: 'text-cobre-700'  },
  alta:        { position: 1.00, chipBg: 'bg-[#A04545]/15', chipFg: 'text-[#7E2F2F]' },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ResultView({
  result, canPurchase, onCompensate, onLeadOptIn, leadCaptured, onRestart, i18n, audience,
}: Props) {
  const scale = BAND_SCALE[result.band] ?? BAND_SCALE.media!;
  const bandLabel = result.band_label ?? result.band;
  const offers = result.compensation_offers ?? [];
  const isOrg = audience === 'organizacao';

  return (
    <div className="space-y-6" aria-live="polite">
      <CountUpHero
        total={result.total_tco2e}
        scale={scale}
        bandLabel={bandLabel}
      />

      {/* Compensar — só em purchase_mode=lead_and_purchase */}
      {canPurchase && offers.length > 0 && (
        <div className="pt-2">
          <p className="text-xs uppercase tracking-wider text-tabaco mb-2">
            Compense com projetos certificados
          </p>
          <OfferCarousel offers={offers} onCompensate={onCompensate} />
        </div>
      )}

      {/* Opt-in email INLINE — só no fluxo participante (mockup 15). No
        fluxo org (mockup 20) o lead já foi capturado no Step 4. */}
      {!isOrg && !leadCaptured && onLeadOptIn && (
        <LeadOptInBox onSubmit={onLeadOptIn} i18n={i18n} />
      )}
      {leadCaptured && (
        <div className="rounded-md border border-floresta/30 bg-floresta/5 px-3 py-2.5 text-xs text-floresta">
          {t(i18n, isOrg ? 'result.lead_thanks_org' : 'result.lead_thanks_participante')}
        </div>
      )}

      <div className="pt-2 flex items-center justify-between">
        <button
          type="button"
          onClick={onRestart}
          className="text-sm text-tabaco hover:text-floresta transition-colors"
        >
          ↺ Refazer cálculo
        </button>
        {result.deduped && (
          <span className="text-2xs text-ink-subtle">
            Mesmos dados nas últimas 24h — resultado anterior.
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Count-up + bar ─────────────────────────────────────────────────────

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Gradient único reutilizado no track e na fill — escala "azul→verde→amarelo→
// laranja→vermelho" sugerida pelo mockup 15, com paleta natural OMA-ish.
// Quando a fill cresce de 0% até `scale.position*100%`, ela revela
// progressivamente o trecho do gradiente correspondente à banda.
const SCALE_GRADIENT =
  'linear-gradient(to right, ' +
  '#4A8C5F 0%, ' +    // verde (muito baixa)
  '#B5A642 38%, ' +   // amarelo-musgo (baixa)
  '#9D764F 65%, ' +   // cobre (média)
  '#A04545 100%)';    // vermelho-tijolo (alta)

function CountUpHero({
  total, scale, bandLabel,
}: {
  total: number;
  scale: { position: number; chipBg: string; chipFg: string };
  bandLabel: string;
}) {
  const reduced = prefersReducedMotion();
  const [displayed, setDisplayed] = useState(reduced ? total : 0);
  const [barProgress, setBarProgress] = useState(reduced ? scale.position : 0);
  const [reachedBand, setReachedBand] = useState(reduced);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (reduced) {
      setDisplayed(total);
      setBarProgress(scale.position);
      setReachedBand(true);
      return;
    }
    const start = performance.now();
    const duration = 2800; // ligeiramente mais lento — mostra a passagem pelas cores
    const ease = (t: number) => 1 - Math.pow(1 - t, 3); // ease-out cubic

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = ease(progress);
      setDisplayed(total * eased);
      // Barra cresce de 0 até a posição da band (não até 100%).
      setBarProgress(scale.position * eased);
      if (progress >= 1) {
        setReachedBand(true);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [total, scale.position, reduced]);

  return (
    <div className="text-center py-6">
      <p className="text-xs uppercase tracking-widest text-tabaco">Sua emissão de carbono</p>
      <p className="font-display text-5xl text-floresta my-3 tabular-nums">
        {formatTCO2e(displayed)}
      </p>

      {/* Escala progressiva — track com gradient completo esmaecido (guia da
        escala 0→100%); fill é o MESMO gradient em 100% width revelado por
        clip-path até `barProgress*100%`, fazendo com que as cores na parte
        preenchida casem exatamente com o gradient do track. */}
      <div className="mx-auto max-w-sm relative h-2.5">
        <div
          className="absolute inset-0 rounded-full"
          style={{ backgroundImage: SCALE_GRADIENT, opacity: 0.18 }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 rounded-full"
          style={{
            backgroundImage: SCALE_GRADIENT,
            clipPath: `inset(0 ${100 - barProgress * 100}% 0 0 round 9999px)`,
            WebkitClipPath: `inset(0 ${100 - barProgress * 100}% 0 0 round 9999px)`,
          }}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(barProgress * 100)}
          aria-label="Intensidade da emissão de carbono"
        />
      </div>

      <span
        className={`mt-3 inline-block px-3 py-1 rounded-full text-2xs uppercase tracking-wider font-medium ${scale.chipBg} ${scale.chipFg} transition-opacity duration-300 ${reachedBand ? 'opacity-100' : 'opacity-0'}`}
      >
        {bandLabel}
      </span>
    </div>
  );
}

// ─── Opt-in email inline ────────────────────────────────────────────────

function LeadOptInBox({
  onSubmit, i18n,
}: {
  onSubmit: (s: SubmitterInput) => void | Promise<void>;
  i18n?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full rounded-lg border border-floresta/30 bg-cream/40 px-4 py-3 text-left transition-colors hover:bg-floresta/5"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-display text-sm text-floresta font-semibold">Receba seu relatório por e-mail</p>
            <p className="text-2xs text-tabaco mt-0.5">Resumo completo + dicas pra reduzir e compensar.</p>
          </div>
          <span className="text-sm text-floresta shrink-0">Receber →</span>
        </div>
      </button>
    );
  }

  async function handleSubmit() {
    if (!EMAIL_RE.test(email.trim())) {
      setError(t(i18n, 'errors.email_required'));
      return;
    }
    if (!consent) {
      setError(t(i18n, 'errors.consent_required'));
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      await onSubmit({
        full_name: name.trim(),
        email: email.trim().toLowerCase(),
        consent: true,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-lg border border-floresta/30 bg-cream/40 p-4 space-y-3">
      <p className="font-display text-sm text-floresta font-semibold">Receba seu relatório por e-mail</p>

      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Seu nome (opcional)"
        maxLength={120}
        className="w-full rounded-md border border-areia bg-white px-3 py-2 text-sm focus:outline-none focus:border-floresta focus:ring-2 focus:ring-floresta/20"
      />

      <input
        type="email"
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (error) setError(undefined); }}
        placeholder="voce@exemplo.com"
        maxLength={160}
        className="w-full rounded-md border border-areia bg-white px-3 py-2 text-sm focus:outline-none focus:border-floresta focus:ring-2 focus:ring-floresta/20"
      />

      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 size-4 accent-floresta shrink-0"
        />
        <span className="text-2xs text-ink leading-relaxed">
          {t(i18n, 'consent_label')}{' '}
          <a
            href="https://omagroup.com.br/politica-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cobre hover:text-cobre-700 underline"
          >
            {t(i18n, 'privacy_link')}
          </a>
          .
        </span>
      </label>

      {error && <p role="alert" className="text-2xs text-cobre-700">{error}</p>}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={submitting}
          className="text-xs text-tabaco hover:text-floresta transition-colors disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-4 py-2 rounded-md bg-floresta text-cream text-sm font-medium hover:bg-floresta-600 disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Enviando…' : 'Receber relatório →'}
        </button>
      </div>
    </div>
  );
}

// ─── Offer carousel ─────────────────────────────────────────────────────

function OfferCarousel({
  offers, onCompensate,
}: {
  offers: InstanceOfferingDetail[];
  onCompensate: (o: InstanceOfferingDetail) => void;
}) {
  if (offers.length === 1) {
    return <OfferCard offer={offers[0]!} onCompensate={onCompensate} />;
  }
  return (
    <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1">
      {offers.map((o) => (
        <div key={o.offering_id} className="snap-start shrink-0 w-[280px]">
          <OfferCard offer={o} onCompensate={onCompensate} />
        </div>
      ))}
    </div>
  );
}

function OfferCard({
  offer, onCompensate,
}: {
  offer: InstanceOfferingDetail;
  onCompensate: (o: InstanceOfferingDetail) => void;
}) {
  return (
    <div className="rounded-lg border border-areia bg-cream/70 overflow-hidden shadow-sm">
      {offer.project_cover_image_url && (
        <div
          className="h-28 bg-floresta/10 bg-center bg-cover"
          style={{ backgroundImage: `url(${offer.project_cover_image_url})` }}
          aria-hidden="true"
        />
      )}
      <div className="p-3 space-y-2">
        <div>
          <p className="text-2xs uppercase tracking-wider text-cobre">
            {offer.project_category_label ?? offer.project_category} · Vintage {offer.vintage_year}
          </p>
          <h4 className="font-display text-base text-floresta leading-tight mt-0.5">
            {offer.project_name}
          </h4>
        </div>
        {offer.project_short_description && (
          <p className="text-2xs text-ink-subtle line-clamp-2">{offer.project_short_description}</p>
        )}
        <div className="flex items-center justify-between pt-1">
          <p className="text-sm text-floresta">
            <span className="font-display font-semibold">{formatBRL(offer.price_brl_per_tco2e)}</span>
            <span className="text-2xs text-tabaco"> / tCO₂e</span>
          </p>
          <button
            type="button"
            onClick={() => onCompensate(offer)}
            className="px-3 py-1.5 rounded-md bg-cobre text-cream text-xs font-medium hover:bg-cobre-700 transition-colors"
          >
            Compensar com este projeto
          </button>
        </div>
      </div>
    </div>
  );
}
